import React, { Fragment, useEffect, useState } from 'react';
import {
  Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { Save } from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { H4, Spinner } from '../../../../../AbstractElements';
import { baseURL } from '../../../../../Services/api/baseURL';

const PAGE_TYPE = 'contact-us';

const ContactUsEditor = () => {
  const [title, setTitle] = useState('Contact Info Box');
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem('token'));

  const fetchPage = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseURL}/api/content-settings/${PAGE_TYPE}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      const data = res.data.data;
      if (data) {
        setTitle(data.title || 'Contact Info Box');
        setContent(data.content || '');
        setLastUpdated(data.updatedAt || null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title,
        pageMode: 'html',
        content,
        sections: [],
        status: 'active',
      };
      const res = await axios.put(
        `${baseURL}/api/content-settings/${PAGE_TYPE}`,
        payload,
        { headers: { Authorization: `${getToken()}` } }
      );
      setLastUpdated(res.data.data?.updatedAt || new Date().toISOString());
      Swal.fire({
        title: 'Contact Us saved!',
        icon: 'success',
        confirmButtonColor: '#d3178a',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Failed to save', icon: 'error', confirmButtonColor: '#d3178a' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Container fluid style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm="12">
            <Card style={{ borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <CardBody style={{ padding: '28px' }}>
                <div
                  className="d-flex justify-content-between align-items-start mb-4"
                  style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px' }}
                >
                  <div>
                    <H4 style={{ margin: 0 }}>Contact Us</H4>
                    {lastUpdated && (
                      <p style={{ color: '#999', fontSize: '13px', margin: '4px 0 0' }}>
                        Last updated: {moment(lastUpdated).format('DD MMM YYYY, hh:mm A')}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleSave}
                    disabled={saving || loading}
                    style={{
                      background: '#d3178a',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: 600,
                    }}
                  >
                    <Save size={16} />
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <FormGroup className="mb-4">
                      <Label style={{ fontWeight: 600, fontSize: '14px' }}>Box Label</Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Contact Info Box"
                        style={{ borderRadius: '8px', maxWidth: '500px' }}
                      />
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label style={{ fontWeight: 600, fontSize: '14px' }}>
                        Box HTML
                      </Label>
                      <p style={{ fontSize: '12px', color: '#999', marginTop: '-4px' }}>
                        Paste or write raw HTML — it renders as the info box (opening hours, address, phone, email…) at the top of the Contact Us page.
                      </p>
                      <Input
                        type="textarea"
                        rows={10}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="<h2>Opening hours</h2><p>Monday to Thursday - 11:00 am to 10:30 pm</p>"
                        style={{ borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px' }}
                      />
                      <div style={{ marginTop: '10px' }}>
                        <Label style={{ fontSize: '12px', color: '#999' }}>Preview</Label>
                        <div
                          style={{
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            padding: '16px',
                            background: '#fcfcfc',
                          }}
                          dangerouslySetInnerHTML={{ __html: content }}
                        />
                      </div>
                    </FormGroup>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ContactUsEditor;
